import React, { useState, useEffect } from "react";
import { authAPI } from "../services/api";
import { toast } from "react-toastify";
import { Loader2 } from "lucide-react";

const ProfilePage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // ================= FETCH PROFILE =================
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const { data } = await authAPI.getProfile();
        setFormData({
          name: data.name || "",
          email: data.email || "",
          phone: data.phone || "",
          password: "",
        });
      } catch (error) {
        console.error("Profile fetch error:", error);
        toast.error("Failed to load profile.");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  // ================= UPDATE PROFILE =================
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error("Name cannot be empty.");
      return;
    }

    if (!/^[6-9]\d{9}$/.test(formData.phone)) {
      toast.error("Please enter a valid 10-digit phone number.");
      return;
    }

    setSaving(true);
    try {
      const { data } = await authAPI.updateProfile({
        name: formData.name,
        phone: formData.phone,
        password: formData.password || undefined,
      });

      localStorage.setItem("userInfo", JSON.stringify(data));
      localStorage.setItem("token", data.token);

      toast.success("Profile updated successfully!");
      setFormData((prev) => ({ ...prev, password: "" }));
    } catch (err) {
      console.error("Profile update error:", err);
      toast.error(
        err.response?.data?.message || "Failed to update profile. Try again."
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100 text-secondary">
        <Loader2 className="me-2 animate-spin" size={24} />
        Loading profile...
      </div>
    );
  }

  return (
    <div
      className="d-flex justify-content-center align-items-center"
      style={{
        minHeight: "calc(100vh - 100px)",
        background: "#f8f9fa",
      }}
    >
      <div
        className="card shadow-lg border-0 p-4"
        style={{
          width: "100%",
          maxWidth: "480px",
          borderRadius: "18px",
          background: "#ffffff",
        }}
      >
        <h3
          className="text-center fw-bold mb-4"
          style={{ color: "#0d6efd", letterSpacing: "0.3px" }}
        >
          Edit Profile
        </h3>

        <form onSubmit={handleSubmit}>
          {/* Name */}
          <div className="mb-3">
            <label className="form-label fw-semibold text-secondary small">
              Full Name
            </label>
            <input
              type="text"
              className="form-control py-2"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              placeholder="Enter your full name"
              required
            />
          </div>

          {/* Email */}
          <div className="mb-3">
            <label className="form-label fw-semibold text-secondary small">
              Email
            </label>
            <input
              type="email"
              className="form-control py-2 bg-light text-muted"
              value={formData.email}
              disabled
              readOnly
            />
          </div>

          {/* Phone */}
          <div className="mb-3">
            <label className="form-label fw-semibold text-secondary small">
              Phone Number
            </label>
            <input
              type="tel"
              className="form-control py-2"
              value={formData.phone}
              onChange={(e) =>
                setFormData({ ...formData, phone: e.target.value })
              }
              placeholder="Enter your 10-digit mobile number"
              required
              pattern="[6-9][0-9]{9}"
              title="Enter a valid 10-digit number starting with 6–9"
            />
          </div>

          {/* Password */}
          <div className="mb-4">
            <label className="form-label fw-semibold text-secondary small">
              New Password <span className="text-muted">(optional)</span>
            </label>
            <input
              type="password"
              className="form-control py-2"
              value={formData.password}
              onChange={(e) =>
                setFormData({ ...formData, password: e.target.value })
              }
              placeholder="Enter new password (leave blank to keep current)"
            />
          </div>

          {/* Save Button */}
          <button
            type="submit"
            className="btn btn-primary w-100 fw-semibold py-2"
            style={{ borderRadius: "10px" }}
            disabled={saving}
          >
            {saving ? (
              <>
                <Loader2 className="me-2 animate-spin" size={18} />
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ProfilePage;
